import { usePermission } from '@/hooks/usePermission';
import { Users } from 'lucide-react';
import React, { useState } from 'react';
import Pagination from '../CommonComponents/Pagination';
import SearchFilter from '../CommonComponents/SearchFilter';
import StatCard from '../CommonComponents/StatCard';

// ----------- Types ------------
interface User {
  id: string;
  name: string;
  mobile: string;
  project: string;
  status: 'Active' | 'Inactive';
  registration: string;
}

// ----------- Mock Data ----------
const users: User[] = new Array(10).fill(null).map((_, i) => ({
  id: `A12${i + 1}`,
  name: `User ${i + 1}`,
  mobile: `+1234567890${i}`,
  project: `Project ${(i % 3) + 1}`,
  status: i % 2 === 0 ? 'Active' : 'Inactive',
  registration: `2025-0${(i % 9) + 1}-10`,
}));

// Default pagination values
const page = 1;
const pageSize = 25;
const totalPages = Math.ceil(users.length / pageSize);

//---------Stat data-------------
const userStats = [
  {
    id: 1,
    title: 'Total Users',
    value: '2,847',
    icon: Users,
    changeColor: 'green' as const,
  },
  {
    id: 2,
    title: 'Projects',
    value: '42',
    icon: Users,
    changeColor: 'green' as const,
  },
  {
    id: 3,
    title: 'Active Users',
    value: '200',
    icon: Users,
    changeColor: 'green' as const,
  },

  {
    id: 4,
    title: 'Inactive Users',
    value: '200',
    icon: Users,
    changeColor: 'green' as const,
  },
];

// ----------- Component ------------
const UserManagement: React.FC = () => {
  const stats = {
    total: users.length,
    projects: new Set(users.map((u) => u.project)).size,
    active: users.filter((u) => u.status === 'Active').length,
    inactive: users.filter((u) => u.status === 'Inactive').length,
  };

  const [filteredUsers, setFilteredUsers] = useState<User[]>(users);
  const [registrationOptions, setRegistrationOptions] = useState<string[]>([
    'All Registrations',
    ...Array.from(new Set(users.map((u) => u.registration))),
  ]);

  const permissions = {
    create: usePermission('write', 'project_users'),
    edit: usePermission('edit', 'project_users'),
    export: usePermission('export', 'project_users'),
    delete: usePermission('delete', 'project_users'),
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-pink-900">User Management</h1>
          <p className="text-sm text-gray-600">Manage all users across all projects.</p>
        </div>

        <div className="flex gap-2 mt-4 md:mt-0">
          <button className="flex items-center gap-2 px-4 py-2 border rounded text-pink-900 bg-white hover:bg-pink-50 transition shadow-sm">
            <span className="text-lg font-bold">＋</span> Add Users
          </button>

          <button
            disabled={permissions.export}
            className="flex items-center gap-2 px-4 py-2 border rounded text-pink-900 bg-white hover:bg-pink-50 transition shadow-sm"
          >
            <span className="text-lg">⬇</span> Export
          </button>
        </div>
      </div>

      {/* User Stats */}
      <div className="bg-white rounded-xl shadow p-4 flex items-start gap-4 w-full">
        {userStats.map((userStats) => (
          <StatCard
            key={userStats.id}
            title={userStats.title}
            value={userStats.value}
            icon={userStats.icon}
            changeColor={userStats.changeColor}
          />
        ))}
      </div>

      {/* Search Filter */}
      <div className=" flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <div className="flex flex-wrap gap-4 mb-4">
          <SearchFilter<User>
            placeholder="Search users, country, projects or status..."
            data={users}
            onFilter={setFilteredUsers}
            keys={['id', 'name', 'mobile', 'project', 'status', 'registration']} // searchable fields
            statusOptions={['All Status', 'Active', 'Inactive']}
            projectOptions={['All Projects', 'Project 1', 'Project 2', 'Project 3']}
            registrationOptions={registrationOptions}
          />
        </div>
      </div>

      {/* User Table */}
      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full table-auto">
          <thead className="bg-pink-800 text-white">
            <tr>
              <th className="px-4 py-2">User Name</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Mobile Number</th>
              <th className="px-4 py-2">Project</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Registration</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, idx) => (
              <tr key={idx} className={idx % 2 ? 'bg-pink-50' : ''}>
                <td className="px-4 py-2">{user.id}</td>
                <td className="px-4 py-2">{user.name}</td>
                <td className="px-4 py-2">{user.mobile}</td>
                <td className="px-4 py-2">
                  <span className="bg-gray-200 px-2 py-1 rounded text-sm">{user.project}</span>
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`px-2 py-1 text-sm rounded ${
                      user.status === 'Active' ? 'bg-green-400 text-white' : 'bg-red-400 text-white'
                    }`}
                  >
                    {user.status}
                  </span>
                </td>
                <td className="px-4 py-2">{user.registration}</td>
                <td className="px-4 py-2 space-x-2">
                  <button>👁️</button>
                  <button>✏️</button>
                  <button>👥</button>
                  <button>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className=" items-center mt-4 text-sm text-gray-600">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
        />
      </div>
    </div>
  );
};

export default UserManagement;
